import { useWriteContract } from "@abraxas/ui/web3"
import { parseEventLogs, parseEther, maxUint128 } from "viem"
import { TransactionReceipt } from "viem"
import { useCallback, useMemo } from "react"
import { config } from "@repo/loomlock/client"
import { MARKETPLACE_CHAIN_ID } from "../marketplace-chain-id"
import { ABIDirectListingsLogic } from "../abis/DirectListingsLogic"
import * as Yup from "yup"
import { useMarketplaceListings } from "./useMarketplaceListings"
import type { ProductType } from "@repo/loomlock/model-types"

export interface CreateListingFormValues {
  price: string
  quantity: number
  endTime?: Date
}

export const createListingSchema = Yup.object({
  price: Yup.number()
    .required("Please enter a price")
    .min(0.0001, "Price must be at least 0.0001 ETH")
    .meta({
      unit: "eth",
      props: {
        step: 0.00001,
        min: 0,
      },
    }),
  // TODO: max on user holdings
  quantity: Yup.number().required().min(1),
  endTime: Yup.date()
    .optional()
    .min(new Date(), "End time must be in the future"),
})

export function useCreateListing({
  tokenId,
  productType,
}: {
  tokenId: string | bigint
  productType: ProductType
}) {
  const { writeContractAsync, ...rest } = useWriteContract("create-listing")

  const initialValues: CreateListingFormValues = useMemo(
    () => ({
      price: "0.0001",
      quantity: 1,
      endTime: undefined,
    }),
    [],
  )
  const { refetch: refetchListings } = useMarketplaceListings()

  const createListing = useCallback(
    async (values: CreateListingFormValues) => {
      const marketplace = config[MARKETPLACE_CHAIN_ID]!.contracts.marketplace
      const contractAddress =
        config[MARKETPLACE_CHAIN_ID]!.contracts[productType!]

      if (!marketplace) {
        throw new Error("Marketplace not deployed on this network")
      }

      if (!contractAddress) {
        throw new Error(`${productType} contract not found`)
      }

      const timestamp = BigInt(Math.floor(Date.now() / 1000))

      const receipt = (await writeContractAsync({
        address: marketplace,
        abi: ABIDirectListingsLogic,
        functionName: "createListing",
        args: [
          {
            assetContract: contractAddress,
            tokenId: BigInt(tokenId),
            quantity: BigInt(values.quantity),
            currency: "0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE",
            pricePerToken: parseEther(String(values.price)),
            startTimestamp: timestamp,
            endTimestamp: values.endTime
              ? BigInt(Math.floor(values.endTime.getTime() / 1000))
              : maxUint128,
            reserved: false,
          },
        ],
        chainId: MARKETPLACE_CHAIN_ID,
        loading: "Creating listing...",
        success: "Listing created successfully!",
        error: "Failed to create listing",
      })) as unknown as TransactionReceipt

      const logs = parseEventLogs({
        logs: receipt.logs,
        abi: ABIDirectListingsLogic,
      })

      const listingCreatedEvent = logs.find(
        (log) => log.eventName === "NewListing",
      )
      const listingId = listingCreatedEvent?.args?.listingId
      refetchListings()
      return {
        receipt,
        listingId,
      }
    },
    [writeContractAsync, tokenId, productType, refetchListings],
  )

  return {
    createListing,
    initialValues,
    validationSchema: createListingSchema,
    ...rest,
  }
}
